import React from "react";
import * as yup from "yup";
import { DialogActions } from "@material-ui/core";
import { TabView, TabPanel } from "primereact/tabview";
import Swal from "sweetalert2";
import { saveVisitDirectSales } from "../../../../../../services/crm/salesJourney";
import SalesItem from "../../../../sales/sales-item/salesItem";
import AppForm from "../../../../../../partials/content/Forms/AppForm";
import DirectSalesHeader from "./directSalesHeader";
import { useGlobalData } from "../../../../../../../provider/GlobalDataProvider";
import { DateTime } from "luxon";

const DirectSalesForm = ({ visit, salesID, onCloseDialog, onConfirmSave }) => {
  const { companyProfile } = useGlobalData();
  let directSales = {
    giStatus: 1,
    invoiceStatus: 1,
    journeyID: visit.journeyID,
    visitID: visit.ID,
    leadID: visit.lead.ID,
    leadName: visit.lead.leadName + " " + visit.lead.contactName,
    customerID: visit.customer.ID,
    customerName: visit.customer.customerName,
    salesID,
    discountID: 0,
    discountName: "",
    salesDate: visit.actVisitDate,
    payMethod: 1,
    cashAccountID: 0,
    payAmount: 0,
    change: 0,
    cardNo: 0,
    cardName: "",
    deadlineDate: DateTime.now().endOf("day"),
    currencyID: companyProfile.currency.ID,
    currencyCode: companyProfile.currency.currencyCode,
    exRateToBaseCurr: 1,
    exRateToForeignCurr: 1,
    subTotal: 0,
    discountPercent: 0,
    discountAmount: 0,
    taxPercent: 0,
    taxAmount: 0,
    addCostTotal: 0,
    grandTotal: 0,
    directSalesItem: [],
    directSalesAddCost: [],
  };
  const directSalesFormSchema = yup.object().shape({
    journeyID: yup.number(),
    leadID: yup.number(),
    leadName: yup.string(),
    customerID: yup.number(),
    customerName: yup.string(),
    salesID: yup.number(),
    salesDate: yup.date(),
    grandTotal: yup.number(),
    cashAccountID: yup.number().min(1, "Cash Account cannot be empty"),
    payAmount: yup.number().when("payMethod", {
      is: (value) => value === 1,
      then: yup
        .number()
        .min(
          yup.ref("grandTotal"),
          "Cash Payment Amount must be the same with Grand Total"
        ),
    }),
    currencyCode: yup.string().required("Currency is required"),
    discountAmount: yup.number(),
    taxAmount: yup.number(),
    directSalesItem: yup.array().of(
      yup.object().shape({
        itemID: yup.number(),
        varCode: yup.string(),
        itemName: yup.string().required("Item is required"),
        qty: yup
          .number()
          .transform((value) => (isNaN(value) ? 0 : value))
          .nullable()
          .required("Quantity is required")
          .min(1, "Quantity cannot be zero or empty")
          .max(yup.ref("stockQty"))
          .default(0),
        stockQty: yup.number(),
        batchID: yup.number(),
        uomID: yup.number(),
        uom: yup.string().when("itemID", {
          is: (value) => value > 0,
          then: yup.string().required(0, "Unit cannot be empty"),
        }),
        unitPrice: yup
          .number()
          .transform((value) => (isNaN(value) ? 0 : value))
          .nullable()
          .required("Unit Price is required")
          .min(1, "Unit Price cannot be zero or empty")
          .default(0),
        discountPercent: yup.number(),
        discountAmount: yup
          .number()
          .transform((value) => (isNaN(value) ? 0 : value))
          .nullable()
          .default(0),
        taxPercent: yup.number(),
        taxAmount: yup
          .number()
          .transform((value) => (isNaN(value) ? 0 : value))
          .nullable()
          .default(0),
        totalPrice: yup
          .number()
          .transform((value) => (isNaN(value) ? 0 : value))
          .nullable()
          .required("Total Price is required")
          .default(0),
      })
    ),
  });

  return (
    <AppForm
      initialValues={directSales}
      validationSchema={directSalesFormSchema}
      onSubmit={async (values, { setStatus, setSubmitting }) => {
        values.discountPercent = parseFloat(values.discountPercent);
        values.discountAmount = parseFloat(values.discountAmount);
        values.taxPercent = parseFloat(values.taxPercent);
        values.taxAmount = parseFloat(values.taxAmount);
        values.subTotal = parseFloat(values.subTotal);
        values.grandTotal = parseFloat(values.grandTotal);
        values.payAmount = parseFloat(values.payAmount);

        values.directSalesItem.forEach((item) => {
          item.qty = parseFloat(item.qty);
          item.unitPrice = parseFloat(item.unitPrice);
          item.discountPercent = parseFloat(item.discountPercent);
          item.discountAmount = parseFloat(item.discountAmount);
          item.taxPercent = parseFloat(item.taxPercent);
          item.taxAmount = parseFloat(item.taxAmount);
          item.totalPrice = parseFloat(item.totalPrice);
        });
        Swal.mixin({
          customClass: {
            confirmButton: "btn btn-success btn-md btn-pill btn-bold",
            cancelButton: "btn btn-danger btn-md btn-pill btn-bold",
          },
          buttonsStyling: false,
        })
          .fire({
            title: "Are you sure want to save this Direct Sales Visit ?",
            text: "Please note that you are responsible for this action",
            icon: "question",
            showCloseButton: true,
            showCancelButton: true,
            confirmButtonText: `<i class="fas fa-check-circle"></i> Yes, save it`,
            cancelButtonText: `<i class="fas fa-times-circle"></i> No, cancel`,
            reverseButtons: true,
          })
          .then((result) => {
            if (result.isConfirmed) {
              saveVisitDirectSales(values).then((response) => {
                Swal.mixin({
                  customClass: {
                    confirmButton: "btn btn-success btn-md btn-pill btn-bold",
                  },
                  buttonsStyling: false,
                }).fire({
                  title: "Saved",
                  html: response.data.message,
                  icon: "success",
                  showCloseButton: true,
                  confirmButtonText: `<i class="fas fa-check-circle"></i> Okay, noted`,
                });
                onConfirmSave(values);
              });
            } else if (result.dismiss === Swal.DismissReason.cancel) {
              Swal.mixin({
                customClass: {
                  confirmButton: "btn btn-success btn-md btn-pill btn-bold",
                },
                buttonsStyling: false,
              }).fire({
                title: "Cancelled",
                text: "No change has been made",
                icon: "info",
                showCloseButton: true,
                confirmButtonText: `<i class="fas fa-check-circle"></i> Okay, noted`,
              });
            }
          });
      }}
    >
      <div className="tabview">
        <div className="card">
          <TabView className="tabview" scrollable>
            <TabPanel header="Direct Sales" rightIcon="fas fa-people-carry">
              <DirectSalesHeader />
            </TabPanel>
          </TabView>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12 col-md-12 col-lg-12">
          <br />
          <SalesItem
            journeyID={visit.journeyID}
            property="directSalesItem"
            propertyAddCost="directSalesAddCost"
          />
        </div>
      </div>
      <DialogActions>
        <button
          type="button"
          className="btn btn-label-danger btn-md btn-pill btn-bold"
          onClick={onCloseDialog}
        >
          <i className="fas fa-window-close"></i>
          Close
        </button>
        <button
          type="submit"
          className="btn btn-success btn-md btn-pill btn-bold"
        >
          <i className="fas fa-save"></i>
          Save
        </button>
      </DialogActions>
    </AppForm>
  );
};

export default DirectSalesForm;
